import { Box, Stack, Typography, useTheme, inputBaseClasses } from "@mui/material";
import React from "react";
import { getLanguageIcon } from "src/components/collections/consts";
import { getMandatoryStar } from "src/components/collections/helpers";
import { StyledTextField, styledTextFieldClasses } from "../text-field/HyroTextField.styles";
import { capitalizeAndCleanUnderscore, toLowerCaseAndCapitalize } from "@utils";
import StatusChip from "src/components/collections/StatusChip";
import FieldCopy from "src/components/widgets/FieldCopyPaste/FieldCopy";

const LanguageTextField = ({
  language,
  value,
  errorText,
  textFieldProps,
  isReadOnly,
  onChange,
  fieldType,
  index,
  testId,
  theme,
  showLanguageIcon = false,
  isMultiLanguage = false
}) => {
  const LanguageIcon = showLanguageIcon ? getLanguageIcon(language) : null;
  const hasError = !!errorText?.[language];
  const fieldValue = value?.[language] ?? "";
  const handleChange = ({ target }) => !isReadOnly && onChange({ ...value, [language]: target.value }, language);
  const handleBlur = ({ target }) => !isReadOnly && onChange({ ...value, [language]: target.value.trim() }, language);

  return (
    <StyledTextField
      {...textFieldProps}
      helperText={hasError ?
        <Typography sx={{ whiteSpace: "pre-line" }} variant="caption">{errorText[language]}</Typography> :
        <StatusChip status={textFieldProps.status} />}
      error={hasError}
      slotProps={{
        htmlInput: {
          autoComplete: "off",
          "data-testid": isMultiLanguage
            ? `${testId ? `${testId}-` : ""}${fieldType}-${language}-${index}`
            : `${testId ? `${testId}-` : ""}${fieldType}-${index}`
        },
        input: {
          sx: { borderRadius: "4px", height: "48px", [`& .${inputBaseClasses.input}`]: { padding: 0 } },
          readOnly: isReadOnly,
          endAdornment: <>
            {fieldValue && <span className={styledTextFieldClasses.copyIcon}>
              <FieldCopy type={"flat"} useWrapper zIndex={theme.zIndex.drawer + 1} iconSize={18} value={fieldValue} />
            </span>}
            {LanguageIcon && <LanguageIcon style={{ marginLeft: "12px" }} />}
          </>
        }
      }}
      value={fieldValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

const HyroMultiLanguageField = ({ value, title, errorText, textFieldProps, isReadOnly, onChange, currentSelectedLanguage, collectionLanguages, fieldType, index, testId, isMandatory }) => {
  const theme = useTheme();
  const isMultiLanguage = collectionLanguages?.length > 1;

  const renderLanguageTextField = (language, languageIndex = index) => (
    <LanguageTextField
      language={language}
      value={value}
      errorText={errorText}
      textFieldProps={textFieldProps}
      isReadOnly={isReadOnly}
      onChange={onChange}
      fieldType={fieldType}
      index={languageIndex}
      testId={testId}
      theme={theme}
      showLanguageIcon={isMultiLanguage}
      isMultiLanguage={isMultiLanguage}
    />
  );

  if (isMultiLanguage) {
    return (
      <Stack gap={2}>
        {Object.keys(value).map((language, languageIndex) => (
          <Box key={languageIndex}>
            {languageIndex !== 0 && (
              <Typography variant={"body2"} color="text.800" sx={{ pb: 1 }}>
                {toLowerCaseAndCapitalize(language) + " " + capitalizeAndCleanUnderscore(title)}{getMandatoryStar(isMandatory)}
              </Typography>
            )}
            {renderLanguageTextField(language, languageIndex)}
          </Box>
        ))}
      </Stack>
    );
  }

  return renderLanguageTextField(currentSelectedLanguage);
};

export default HyroMultiLanguageField;
